// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { DiscussionModel, DiscussionEntityType } from '../../types';
import { createId } from '../../index';

export interface DiscussionFilters {
  orgId: string;
  workspaceId?: string;
  userId?: string;
  entityType?: DiscussionEntityType;
  entityId?: string;
  parentId?: string;
  isDeleted?: boolean;
}

export interface CreateDiscussionInput {
  content: string;
  entityType: DiscussionEntityType;
  entityId: string;
  orgId: string;
  workspaceId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  parentId?: string;
  contentHtml?: string;
  metadata?: Record<string, any>;
}

export interface UpdateDiscussionInput {
  id: string;
  updates: Partial<DiscussionModel>;
}

export class DiscussionOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: DiscussionFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<DiscussionModel> {
    const collection = this.db.collections.discussions;

    // Use $ne: true instead of $eq: false to match documents where isDeleted is false, undefined, or missing
    const selector: any = {
      orgId: { $eq: filters.orgId },
      isDeleted: filters.isDeleted === true ? { $eq: true } : { $ne: true },
    };

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.entityType) {
      selector.entityType = { $eq: filters.entityType };
    }

    if (filters.entityId) {
      selector.entityId = { $eq: filters.entityId };
    }

    if (filters.parentId !== undefined) {
      // Support null parentId to query for root discussions
      selector.parentId = filters.parentId === null
        ? { $eq: null }
        : { $eq: filters.parentId };
    }

    const queryOptions: QueryOptions<DiscussionModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    return new QueryOperator<DiscussionModel>(collection, queryOptions);
  }

  async findOne(discussionId: string): Promise<DiscussionModel | null> {
    const doc = await this.db.collections.discussions.findOne(discussionId).exec();
    return doc ? (doc.toJSON() as DiscussionModel) : null;
  }

  async count(filters: DiscussionFilters): Promise<number> {
    const collection = this.db.collections.discussions;
    // Use $ne: true instead of $eq: false to match documents where isDeleted is false, undefined, or missing
    const selector: any = {
      orgId: { $eq: filters.orgId },
      isDeleted: filters.isDeleted === true ? { $eq: true } : { $ne: true },
    };

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.entityType) {
      selector.entityType = { $eq: filters.entityType };
    }

    if (filters.entityId) {
      selector.entityId = { $eq: filters.entityId };
    }

    if (filters.parentId !== undefined) {
      selector.parentId = filters.parentId === null
        ? { $eq: null }
        : { $eq: filters.parentId };
    }

    return await collection.count({ selector }).exec();
  }

  createMutation(): MutationOperator<DiscussionModel, CreateDiscussionInput> {
    return new MutationOperator<DiscussionModel, CreateDiscussionInput>(this.db.collections.discussions, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString();

        // Calculate thread depth based on parent
        let threadDepth = 0;
        if (input.parentId) {
          const parent = await collection.findOne(input.parentId).exec();
          if (parent) {
            const parentData = parent.toJSON() as DiscussionModel;
            threadDepth = parentData.threadDepth + 1;
          }
        }

        const discussion: DiscussionModel = {
          id: createId('discussion'),
          orgId: input.orgId,
          workspaceId: input.workspaceId,
          userId: input.userId,
          userName: input.userName,
          userAvatar: input.userAvatar,
          entityType: input.entityType,
          entityId: input.entityId,
          content: input.content,
          contentHtml: input.contentHtml,
          parentId: input.parentId,
          threadDepth,
          isEdited: false,
          isDeleted: false,
          attachments: [],
          metadata: input.metadata,
          createdAt: now,
          updatedAt: now,
        };

        await collection.insert(discussion);
        return discussion;
      },
    });
  }

  updateMutation(): MutationOperator<DiscussionModel, UpdateDiscussionInput> {
    return new MutationOperator<DiscussionModel, UpdateDiscussionInput>(this.db.collections.discussions, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec();
        if (!doc) throw new Error(`Discussion ${id} not found`);

        const now = new Date().toISOString();
        const updateData: any = {
          ...updates,
          updatedAt: now
        };

        // Track edit metadata if content changed
        const oldData = doc.toJSON() as DiscussionModel;
        if (updates.content && updates.content !== oldData.content) {
          updateData.isEdited = true;
          updateData.editedAt = now;
        }

        await doc.update({
          $set: updateData,
        });

        return doc.toJSON() as DiscussionModel;
      },
    });
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.discussions, {
      mutationFn: async (collection, discussionId) => {
        const doc = await collection.findOne(discussionId).exec();
        if (!doc) throw new Error(`Discussion ${discussionId} not found`);

        const now = new Date().toISOString();
        await doc.update({
          $set: {
            isDeleted: true,
            deletedAt: now,
            updatedAt: now
          },
        });
      },
    });
  }
}
